<template>
  <section class="team-competency-page">
    <PageHeader
        :back-route="{
          name: 'user.my-team.member',
          params: { userId: String(currentUserId || '') },
        }"
        :title="assignment?.name || $t('general.competency_detail')"
        icon="uil-award"
    />

    <CompetencyDetailSkeleton v-if="loading" :show-member-context="true"/>

    <div v-else-if="notFound" class="team-detail-state team-detail-state--empty" role="alert">
      <i aria-hidden="true" class="uil-exclamation-circle"></i>
      <div>
        <strong>{{ $t('general.competency_not_found') }}</strong>
        <p>{{ $t('general.competency_not_found_hint') }}</p>
      </div>
      <router-link :to="{ name: 'user.my-team' }" class="team-state-link">
        {{ $t('general.back_to_my_team') }}
      </router-link>
    </div>

    <div v-else-if="pageError" class="team-detail-state team-detail-state--error" role="alert">
      <i aria-hidden="true" class="uil-exclamation-triangle"></i>
      <div>
        <strong>{{ $t('general.competency_detail_load_failed') }}</strong>
        <p>{{ pageError }}</p>
      </div>
      <button class="team-state-button" type="button" @click="loadDetail">
        {{ $t('general.try_again') }}
      </button>
    </div>

    <template v-else-if="member && assignment">
      <section :aria-label="$t('general.evaluated_team_member')" class="team-member-context">
        <UserProfileAvatar :name="memberName"
                           :photo="member.avatar || ''"
                           class="team-member-avatar"
        />
        <div class="team-member-context__identity">
          <span>{{ $t('general.evaluated_team_member') }}</span>
          <strong>{{ memberName }}</strong>
          <small>{{ organizationSummary }}</small>
        </div>
        <router-link
            :to="{
            name: 'user.my-team.member',
            params: { userId: String(currentUserId) },
          }"
            class="team-member-context__link"
        >
          {{ $t('general.all_competencies') }}
        </router-link>
      </section>

      <AlertMessage v-if="submitError"
                    :message="{ type: 'error', text: submitError }"
                    @remove="submitError = ''"
      />
      <AlertMessage v-if="successMessage"
                    :message="{ type: 'success', text: successMessage }"
                    @remove="successMessage = ''"
      />

      <AssignedCompetencyDetail :key="detailViewKey"
                                :assignment="assignment"
                                :can-comment="true"
                                :comments="comments"
                                :submitting="submittingComment"
                                viewer="manager"
                                @open-development-plan="openDevelopmentPlan"
                                @submit-comment="submitManagerComment"
      />
    </template>
    <DevelopmentPlanModal :visible="developmentPlanVisible"
                          :submitting="submittingDevelopmentPlan"
                          :error="developmentPlanError"
                          @close="closeDevelopmentPlan"
                          @submit="saveDevelopmentPlan"
    />
  </section>
</template>

<script>
import PageHeader from "@/view/components/common/PageHeader.vue";
import AlertMessage from "@/view/components/notification/AlertMessage.vue";
import UserProfileAvatar from "@/view/components/common/UserProfileAvatar.vue";
import AssignedCompetencyDetail from "@/view/components/developmentCompetencies/AssignedCompetencyDetail.vue";
import DevelopmentPlanModal from "@/view/components/developmentCompetencies/DevelopmentPlanModal.vue";
import CompetencyDetailSkeleton from "@/view/components/developmentCompetencies/CompetencyDetailSkeleton.vue";
import ApiService from "@/core/services/api.service";
import querystring from "querystring";
import widgetMixin from "@/core/mixin/widget.mixin";

export default {
  name: "MyTeamCompetencyDetail",
  mixins: [widgetMixin],
  components: {
    AlertMessage,
    UserProfileAvatar,
    AssignedCompetencyDetail,
    DevelopmentPlanModal,
    CompetencyDetailSkeleton,
    PageHeader,
  },
  props: {
    userId: {
      type: [String, Number],
      default: null,
    },
    userCompetencyId: {
      type: [String, Number],
      default: null,
    },
  },
  data() {
    return {
      member: null,
      assignment: null,
      comments: [],
      loading: true,
      notFound: false,
      pageError: "",
      submitError: "",
      successMessage: "",
      submittingComment: false,
      detailViewKey: 0,
      requestSequence: 0,
      developmentPlanVisible: false,
      developmentPlanError: "",
      submittingDevelopmentPlan: false,
      developmentPlanRequestSequence: 0,
    };
  },
  computed: {
    currentUserId() {
      return this.userId !== null && this.userId !== undefined
          ? this.userId
          : this.$route.params.userId;
    },
    currentUserCompetencyId() {
      return this.userCompetencyId !== null && this.userCompetencyId !== undefined
          ? this.userCompetencyId
          : this.$route.params.userCompetencyId;
    },
    memberName() {
      return this.member?.full_name || this.$t("general.team_member");
    },
    organizationSummary() {
      if (!this.member) return "";
      return [
        this.displayValue(this.member.department_name),
        this.displayValue(this.member.branch_name),
        this.displayValue(this.member.title_name),
      ]
          .filter((value) => value !== "—")
          .join(" · ");
    },
  },
  watch: {
    currentUserId() {
      this.loadDetail();
    },
    currentUserCompetencyId(newValue, oldValue) {
      if (newValue !== oldValue) this.loadDetail();
    },
  },
  async mounted() {
    // Widget kapalıysa sayfa erişime kapalı (sol menü ile aynı kural)
    try {
      await this.getWidgetsMixin();

      if (!this.isMyTeamActiveMixin) {
        await this.$router.push({ path: "/dashboard" });
        return;
      }

      await this.loadDetail();
    } catch (error) {
      await this.$router.push({ path: "/dashboard" });
    }
  },
  beforeDestroy() {
    this.requestSequence += 1;
    this.resetDevelopmentPlan();
  },
  methods: {
    hasValidIds() {
      const userId = String(this.currentUserId ?? "").trim();
      const competencyId = String(this.currentUserCompetencyId ?? "").trim();
      return (
          userId.length > 0 &&
          userId.length <= 200 &&
          competencyId.length > 0 &&
          competencyId.length <= 240
      );
    },
    applyCompetencyPayload(payload) {
      const item = payload?.data || payload;
      if (!item || typeof item !== "object" || item.id == null) return false;

      this.assignment = {
        id: item.id,
        name: item.name,
        description: item.description,
        assigned_at: item.assigned_at,
        updated_at: item.updated_at,
        comments_count: item.comments_count,
        latest_target: item.latest_target ?? null,
        has_target: item.has_target === true || !!item.latest_target,
        target_year: Number(item.target_year) || new Date().getFullYear(),
      };
      this.comments = Array.isArray(item.comments) ? item.comments : [];
      return true;
    },
    async loadDetail(showLoader = true) {
      const requestId = ++this.requestSequence;
      if (showLoader) {
        this.resetDevelopmentPlan();
        this.loading = true;
        this.member = null;
        this.assignment = null;
        this.comments = [];
        this.submitError = "";
        this.successMessage = "";
      }
      this.notFound = false;
      this.pageError = "";

      if (!this.hasValidIds()) {
        this.loading = false;
        this.notFound = true;
        return;
      }

      try {
        const [detailResponse, memberResponse] = await Promise.all([
          ApiService.get(
              `api/my-team/${this.currentUserId}/${this.currentUserCompetencyId}`
          ),
          ApiService.get(
              `api/my-team/${this.currentUserId}?${querystring.stringify({
                page: 1,
                perPage: 1,
              })}`
          ),
        ]);

        if (requestId !== this.requestSequence) return;

        const ok = this.applyCompetencyPayload(detailResponse?.data);
        if (!ok) {
          this.notFound = true;
          return;
        }

        const memberPayload = memberResponse?.data || {};
        if (memberPayload.user && typeof memberPayload.user === "object") {
          this.member = memberPayload.user;
        } else {
          this.member = { id: this.currentUserId };
        }
      } catch (error) {
        if (requestId !== this.requestSequence) return;
        if (error?.response?.status === 404) {
          this.notFound = true;
        } else {
          this.pageError =
              error?.response?.data?.message ||
              error?.message ||
              this.$t("general.try_later");
        }
      } finally {
        if (requestId === this.requestSequence) this.loading = false;
      }
    },
    resetDevelopmentPlan() {
      this.developmentPlanRequestSequence += 1;
      this.developmentPlanVisible = false;
      this.developmentPlanError = "";
      this.submittingDevelopmentPlan = false;
    },
    openDevelopmentPlan() {
      if (!this.assignment || this.assignment.has_target || this.assignment.latest_target || this.submittingDevelopmentPlan) return;
      this.developmentPlanError = "";
      this.developmentPlanVisible = true;
    },
    closeDevelopmentPlan() {
      if (!this.submittingDevelopmentPlan) this.resetDevelopmentPlan();
    },
    isCurrentDevelopmentPlanRequest(requestId, userId, competencyId, year) {
      return requestId === this.developmentPlanRequestSequence &&
        userId === String(this.currentUserId) &&
        competencyId === String(this.currentUserCompetencyId) &&
        year === (Number(this.assignment?.target_year) || new Date().getFullYear());
    },
    async saveDevelopmentPlan(text) {
      if (this.submittingDevelopmentPlan || !this.developmentPlanVisible || !this.hasValidIds()) return;
      if (!this.assignment || this.assignment.has_target || this.assignment.latest_target) return;
      const comment = String(text || "").trim();
      if (!comment) {
        this.developmentPlanError = this.$t("general.development_plan_required");
        return;
      }

      const userId = String(this.currentUserId);
      const competencyId = String(this.currentUserCompetencyId);
      const year = Number(this.assignment?.target_year) || new Date().getFullYear();
      const requestId = ++this.developmentPlanRequestSequence;
      this.submittingDevelopmentPlan = true;
      this.developmentPlanError = "";
      this.successMessage = "";

      try {
        const response = await ApiService.post(`api/my-team/${userId}/${competencyId}/target`, { comment, year });
        if (!this.isCurrentDevelopmentPlanRequest(requestId, userId, competencyId, year)) return;

        const target = response?.data?.data || response?.data;
        if (target?.id != null) {
          this.assignment = { ...this.assignment, latest_target: target, has_target: true };
        }
        await this.loadDetail(false);
        if (!this.isCurrentDevelopmentPlanRequest(requestId, userId, competencyId, year)) return;
        this.developmentPlanVisible = false;
        this.successMessage = this.$t("general.development_plan_saved");
      } catch (error) {
        if (!this.isCurrentDevelopmentPlanRequest(requestId, userId, competencyId, year)) return;
        this.developmentPlanError = error?.response?.data?.errors?.comment?.[0] ||
          error?.response?.data?.message || this.$t("general.development_plan_save_failed");
      } finally {
        if (this.isCurrentDevelopmentPlanRequest(requestId, userId, competencyId, year)) {
          this.submittingDevelopmentPlan = false;
        }
      }
    },
    async submitManagerComment(text) {
      if (this.submittingComment || !this.hasValidIds()) return;

      const comment = String(text || "").trim();
      this.submitError = "";
      this.successMessage = "";

      if (!comment) {
        this.submitError = this.$t("general.comment_required");
        return;
      }

      const requestedUserId = String(this.currentUserId);
      const requestedCompetencyId = String(this.currentUserCompetencyId);
      this.submittingComment = true;

      try {
        const url = `api/my-team/${requestedUserId}/${requestedCompetencyId}/comment`;
        const body = { comment };

        await ApiService.post(url, body);

        if (
            requestedUserId !== String(this.currentUserId) ||
            requestedCompetencyId !== String(this.currentUserCompetencyId)
        ) {
          return;
        }

        // Soft refresh: loading skeleton ile remount olmasın (textarea yeniden dolmasın)
        await this.loadDetail(false);
        this.successMessage = this.$t("general.manager_comment_added");
      } catch (error) {
        if (
            requestedUserId !== String(this.currentUserId) ||
            requestedCompetencyId !== String(this.currentUserCompetencyId)
        ) {
          return;
        }
        this.submitError =
            error?.response?.data?.message ||
            error?.response?.data?.errors?.comment?.[0] ||
            error?.message ||
            this.$t("general.comment_add_failed");
      } finally {
        if (
            requestedUserId === String(this.currentUserId) &&
            requestedCompetencyId === String(this.currentUserCompetencyId)
        ) {
          this.submittingComment = false;
        }
      }
    },
    displayValue(value) {
      return value === undefined || value === null || String(value).trim() === ""
          ? "—"
          : String(value);
    },
  },
};
</script>

<style scoped>
.team-competency-page {
  width: 100%;
  max-width: 100%;
  min-width: 0;
  padding: 8px 0 28px;
  box-sizing: border-box;
  overflow-x: hidden;
}

.team-member-context {
  display: flex;
  width: 100%;
  box-sizing: border-box;
  align-items: center;
  gap: 13px;
  padding: 15px 18px;
  margin-bottom: 16px;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
}

.team-member-avatar {
  width: 48px;
  height: 48px;
  min-width: 48px;
  flex-shrink: 0;
  font-size: 15px;
}

.team-member-context__identity {
  min-width: 0;
  flex: 1;
}

.team-member-context__identity span,
.team-member-context__identity strong,
.team-member-context__identity small {
  display: block;
}

.team-member-context__identity span {
  color: #94a3b8;
  font-size: 10px;
  font-weight: 600;
  text-transform: uppercase;
}

.team-member-context__identity strong {
  margin: 2px 0;
  color: #1e293b;
  font-size: 15px;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.team-member-context__identity small {
  color: #64748b;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.team-member-context__link {
  flex: 0 0 auto;
  padding: 8px 12px;
  border: 1px solid rgba(102, 108, 255, 0.25);
  border-radius: 8px;
  color: var(--backgroundColor, #666cff);
  background: rgba(102, 108, 255, 0.07);
  font-size: 12px;
  font-weight: 600;
  text-decoration: none;
  white-space: nowrap;
  -webkit-tap-highlight-color: transparent;
}

.team-detail-state {
  min-height: 260px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  padding: 32px;
  color: #64748b;
  text-align: center;
  background: #fff;
  border: 1px solid #e8edf5;
  border-radius: 12px;
}

.team-detail-state--empty {
  flex-direction: column;
}

.team-detail-state--error {
  flex-wrap: wrap;
}

.team-detail-state > i {
  color: #94a3b8;
  font-size: 38px;
}

.team-detail-state--error > i {
  color: #dc2626;
}

.team-detail-state strong {
  display: block;
  color: #334155;
}

.team-detail-state p {
  margin: 4px 0 0;
}

.team-state-button,
.team-state-link {
  padding: 9px 14px;
  border: 0;
  border-radius: 8px;
  color: #fff;
  background: var(--backgroundColor, #666cff);
  cursor: pointer;
  text-decoration: none;
}

@media (max-width: 800px) {
  .team-competency-page {
    padding-top: 4px;
  }

  .team-member-context {
    flex-wrap: wrap;
    gap: 10px;
    padding: 12px;
    margin-bottom: 12px;
  }

  .team-member-avatar {
    width: 40px;
    height: 40px;
    min-width: 40px;
    font-size: 13px;
  }

  .team-member-context__identity strong {
    font-size: 14px;
  }

  .team-member-context__identity small {
    font-size: 11px;
  }

  .team-member-context__link {
    width: 100%;
    text-align: center;
    min-height: 36px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }

  .team-detail-state {
    min-height: 180px;
    padding: 20px;
  }

  .team-detail-state--error {
    flex-direction: column;
    padding: 24px 16px;
  }

  .team-state-button,
  .team-state-link {
    min-height: 48px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    box-sizing: border-box;
  }
}

@media (max-width: 480px) {
  .team-competency-page {
    padding-bottom: 20px;
  }

  .team-member-context {
    gap: 8px;
    padding: 10px;
  }

  .team-member-context__identity strong {
    font-size: 13px;
  }

  .team-member-context__identity small {
    max-width: 100%;
  }

  .team-member-context__link {
    min-height: 44px;
  }
}
</style>
